import { useEffect, useRef, useState } from "react";
import { GripVertical, Pencil, Plus, Trash2, Copy, ChevronDown } from "lucide-react";
import { toast } from "sonner";
import api, { formatApiErrorDetail } from "./adminApi";
import { imgUrl } from "../lib/spa";
import { Switch } from "../components/ui/switch";
import { TherapistForm } from "./TherapistForm";

export default function AdminTherapists() {
  const [items, setItems] = useState([]);
  const [massages, setMassages] = useState([]);
  const [editing, setEditing] = useState(null); // null | "new" | therapist
  const [duplicating, setDuplicating] = useState(null); // null | therapist source
  const [dragIndex, setDragIndex] = useState(null);
  const [overIndex, setOverIndex] = useState(null);
  const [dupMenuOpen, setDupMenuOpen] = useState(false);
  const dupMenuRef = useRef(null);

  const load = () => {
    api.get("/admin/therapists").then((r) => setItems(r.data));
    api.get("/admin/massages").then((r) => setMassages(r.data));
  };
  useEffect(load, []);

  // Close duplicate menu on outside click
  useEffect(() => {
    if (!dupMenuOpen) return undefined;
    const onDown = (e) => {
      if (dupMenuRef.current && !dupMenuRef.current.contains(e.target)) {
        setDupMenuOpen(false);
      }
    };
    document.addEventListener("mousedown", onDown);
    return () => document.removeEventListener("mousedown", onDown);
  }, [dupMenuOpen]);

  const quickUpdate = async (th, patch) => {
    setItems((list) => list.map((x) => (x.slug === th.slug ? { ...x, ...patch } : x)));
    try {
      await api.put(`/admin/therapists/${th.slug}`, patch);
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail) || "Erro ao atualizar");
      load();
    }
  };

  const handleDrop = async (targetIndex) => {
    if (dragIndex === null || dragIndex === targetIndex) {
      setDragIndex(null);
      setOverIndex(null);
      return;
    }
    const next = [...items];
    const [moved] = next.splice(dragIndex, 1);
    next.splice(targetIndex, 0, moved);
    setItems(next);
    setDragIndex(null);
    setOverIndex(null);
    try {
      await api.put("/admin/therapists-reorder", { slugs: next.map((x) => x.slug) });
      toast.success("Ordem guardada");
    } catch (err) {
      toast.error("Erro ao guardar a ordem");
      load();
    }
  };

  const remove = async (th) => {
    if (!window.confirm(`Apagar a terapeuta ${th.name}? Esta ação é irreversível.`)) return;
    try {
      await api.delete(`/admin/therapists/${th.slug}`);
      toast.success(`${th.name} apagada`);
      load();
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail) || "Erro ao apagar");
    }
  };

  return (
    <div data-testid="admin-therapists-page">
      <div className="flex items-center justify-between">
        <div>
          <h1 className="font-display text-3xl font-light text-white">Terapeutas</h1>
          <p className="mt-1 text-xs text-white/40">Arraste as linhas para reordenar</p>
        </div>
        <div className="flex items-center gap-2">
          {/* Duplicate menu */}
          <div className="relative" ref={dupMenuRef}>
            <button
              type="button"
              data-testid="admin-duplicate-therapist"
              onClick={() => setDupMenuOpen((v) => !v)}
              disabled={items.length === 0}
              className="flex items-center gap-2 rounded-full border border-white/15 px-4 py-2.5 text-xs font-medium uppercase tracking-[0.2em] text-white/70 transition-colors hover:border-gold hover:text-gold disabled:cursor-not-allowed disabled:opacity-40"
            >
              <Copy className="h-4 w-4" strokeWidth={1.5} />
              Duplicar
              <ChevronDown className={`h-3.5 w-3.5 transition-transform ${dupMenuOpen ? "rotate-180" : ""}`} />
            </button>
            {dupMenuOpen && (
              <div className="absolute right-0 top-full z-20 mt-1 max-h-72 w-64 overflow-y-auto rounded-xl border border-white/10 bg-[#101010] p-1 shadow-2xl">
                {items.map((th) => (
                  <button
                    key={th.slug}
                    type="button"
                    data-testid={`admin-duplicate-${th.slug}`}
                    onClick={() => {
                      setDuplicating(th);
                      setDupMenuOpen(false);
                    }}
                    className="flex w-full items-center gap-3 rounded-lg px-3 py-2 text-left text-sm text-white/80 transition-colors hover:bg-gold/10 hover:text-gold"
                  >
                    <img
                      src={imgUrl(th.profile)}
                      alt=""
                      className="h-8 w-8 rounded-md object-cover"
                    />
                    <span className="truncate">{th.name}</span>
                  </button>
                ))}
                {items.length === 0 && (
                  <p className="p-3 text-center text-sm text-white/40">Sem terapeutas para duplicar</p>
                )}
              </div>
            )}
          </div>

          <button
            data-testid="admin-new-therapist"
            onClick={() => setEditing("new")}
            className="flex items-center gap-2 rounded-full bg-gold px-5 py-2.5 text-xs font-semibold uppercase tracking-[0.2em] text-black transition-colors hover:bg-gold-light"
          >
            <Plus className="h-4 w-4" />
            Nova Terapeuta
          </button>
        </div>
      </div>

      <div className="mt-8 overflow-hidden rounded-2xl border border-white/10">
        {items.map((th, i) => (
          <div
            key={th.slug}
            data-testid={`admin-therapist-row-${th.slug}`}
            draggable
            onDragStart={() => setDragIndex(i)}
            onDragOver={(e) => {
              e.preventDefault();
              setOverIndex(i);
            }}
            onDragLeave={() => setOverIndex(null)}
            onDrop={() => handleDrop(i)}
            onDragEnd={() => {
              setDragIndex(null);
              setOverIndex(null);
            }}
            className={`flex flex-wrap items-center gap-4 border-b border-white/5 bg-[#121212] px-4 py-3 last:border-0 transition-colors ${
              th.active === false ? "opacity-50" : ""
            } ${overIndex === i && dragIndex !== null && dragIndex !== i ? "border-t-2 border-t-gold bg-gold/5" : ""} ${
              dragIndex === i ? "opacity-40" : ""
            }`}
          >
            <span data-testid={`admin-therapist-drag-${th.slug}`} className="cursor-grab text-white/30 active:cursor-grabbing">
              <GripVertical className="h-5 w-5" strokeWidth={1.5} />
            </span>
            <img src={imgUrl(th.profile)} alt={th.name} className="h-14 w-12 rounded-lg object-cover" />
            <div className="flex-1 min-w-0">
              <p className="font-medium text-white">{th.name}</p>
              <p className="truncate text-xs text-white/40">
                {th.massages?.length || 0} massagens · {th.gallery?.length || 0} fotos na galeria · ordem {th.order}
              </p>
            </div>
            <label className="flex items-center gap-2 text-[11px] uppercase tracking-wider text-white/50">
              <Switch
                data-testid={`admin-available-toggle-${th.slug}`}
                checked={!!th.available_today}
                onCheckedChange={(v) => quickUpdate(th, { available_today: v })}
              />
              Hoje
            </label>
            <label className="flex items-center gap-2 text-[11px] uppercase tracking-wider text-white/50">
              <Switch
                data-testid={`admin-active-toggle-${th.slug}`}
                checked={th.active !== false}
                onCheckedChange={(v) => quickUpdate(th, { active: v })}
              />
              Ativa
            </label>
            <button
              data-testid={`admin-edit-${th.slug}`}
              onClick={() => setEditing(th)}
              className="flex items-center gap-1.5 rounded-full border border-white/15 px-4 py-2 text-xs text-white/70 transition-colors hover:border-gold hover:text-gold"
            >
              <Pencil className="h-3.5 w-3.5" strokeWidth={1.5} />
              Editar
            </button>
            <button
              data-testid={`admin-delete-${th.slug}`}
              onClick={() => remove(th)}
              className="flex items-center gap-1.5 rounded-full border border-white/15 px-4 py-2 text-xs text-white/70 transition-colors hover:border-red-500 hover:text-red-400"
            >
              <Trash2 className="h-3.5 w-3.5" strokeWidth={1.5} />
              Apagar
            </button>
          </div>
        ))}
        {items.length === 0 && <p className="p-8 text-center text-sm text-white/40">Sem terapeutas.</p>}
      </div>

      {(editing !== null || duplicating !== null) && (
        <TherapistForm
          therapist={editing && editing !== "new" ? editing : null}
          duplicateFrom={duplicating}
          massages={massages}
          onClose={() => {
            setEditing(null);
            setDuplicating(null);
          }}
          onSaved={() => {
            setEditing(null);
            setDuplicating(null);
            load();
          }}
        />
      )}
    </div>
  );
}
